import Box from "@/common/components/Box";
import CreateDiagnosticObjectivePiece from "./CreateDiagnosticObjectivePiece";
import ResumeItem from "@/common/components/ResumeItem";
import { formatCurrency, formatUnit } from "@/common/utils/formatters";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { WrenchIcon } from "lucide-react";
import { useMemo } from "react";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { MaintenanceDirectiveObjectiveData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import MaintenancePieceAdditionalInformationAccordion from "@/features/maintenance/features/workflows/features/diagnosticEntry/screens/Update/components/MaintenancePieceAdditionalInformationAccordion";
import UpdateDiagnosticObjectivePiece from "./UpdateDiagnosticObjectiveActivity";
import DeleteDiagnosticObjectivePiece from "./DeleteDiagniosticObjectiveActivity";

interface DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePiecesProps {
  diagnosticObjective: MaintenanceDirectiveObjectiveData;
}

const DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces = ({
  diagnosticObjective,
}: DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePiecesProps) => {
  const { workflowId, forwardingId } = useParams();

  const queryClient = useQueryClient();

  const forwardingState = queryClient.getQueryState(['maintenanceForwarding', forwardingId]);

  const piecesCountDisplay = useMemo(() => {
    const count = diagnosticObjective.diagnostic_pieces?.length || 0;

    if (count === 0) {
      return 'Nenhuma peça';
    }

    if (count === 1) {
      return '1 Peça';
    }

    return `${count} Peças`;
  }, [forwardingState?.dataUpdatedAt]);

  const totalValueSum = useMemo(() => {
    return diagnosticObjective.diagnostic_pieces?.reduce((prev, curr) => {
      return prev + Number(curr.total_value);
    }, 0) || 0;
  }, [forwardingState?.dataUpdatedAt]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <p className="font-bold text-base">
            Peças
          </p>

          <div className="flex items-center gap-0.5 text-xs">
            <span>
              {piecesCountDisplay}
            </span>

            <span>
              •
            </span>

            <span className="text-valid">
              {formatCurrency(totalValueSum)}
            </span>
          </div>
        </div>

        <CreateDiagnosticObjectivePiece
          diagnosticObjectiveId={diagnosticObjective.id}
          diagnosticObjectivePartId={diagnosticObjective.part?.id}
          selectedPiecesIds={diagnosticObjective.diagnostic_pieces?.map((diagnosticPiece) => diagnosticPiece.piece.id) || []}
        />
      </div>

      <div className="flex flex-col gap-3">
        {diagnosticObjective.diagnostic_pieces?.map((diagnosticPiece) => (
          <Box key={diagnosticPiece.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-3">
                <div className="flex gap-4">
                  <ImageCarousel
                    files={diagnosticPiece.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
                    Placeholder={(
                      <ImageWithPlaceholder
                        className="size-28 rounded-md"
                        iconClassName="size-10"
                        placeholderIcon={WrenchIcon}
                      />
                    )}
                    className="size-28 bg-[#fff]"
                    imageClassName="object-contain"
                    showExpandButton
                    showNavigationButtons={false}
                    showRemoveButton={false}
                  />

                  <div className="flex flex-col gap-2">
                    <p className="text-base font-bold">
                      {diagnosticPiece?.piece?.display_name || '--'}
                    </p>

                    <div className="flex items-center flex-wrap gap-3">
                      <ResumeItem
                        className="text-sm"
                        label="Valor unt."
                        value={formatCurrency(Number(diagnosticPiece?.unit_price) || 0)}
                      />

                      <p className="text-sm">x</p>

                      <ResumeItem
                        className="text-sm"
                        label="Qntd"
                        value={formatUnit(diagnosticPiece.quantity, 'unit')}
                      />

                      <p className="text-sm">=</p>

                      <ResumeItem
                        className="text-sm"
                        label="Valor total"
                        value={formatCurrency(Number(diagnosticPiece?.total_value) || 0)}
                      />

                      <ResumeItem
                        className="text-sm"
                        label="Garantia"
                        value={`${diagnosticPiece.warranty_in_days} Dias`}
                      />

                      <ResumeItem
                        className="text-sm"
                        label="Código da peça"
                        value={diagnosticPiece?.piece?.number || '--'}
                      />

                      <ResumeItem
                        className="text-sm"
                        label="Fabricante"
                        value={diagnosticPiece?.piece?.brand?.display_name || '--'}
                      />
                    </div>

                    <ResumeItem
                      className="text-sm"
                      label="Descrição"
                      value={diagnosticPiece.description}
                    />
                  </div>
                </div>
              </div>

              <div className="ml-auto flex gap-3">
                <DeleteDiagnosticObjectivePiece
                  diagnosticPiece={diagnosticPiece}
                />

                <UpdateDiagnosticObjectivePiece
                  diagnosticPiece={diagnosticPiece}
                />
              </div>
            </div>

            <MaintenancePieceAdditionalInformationAccordion
              workflowId={workflowId}
              pieceId={diagnosticPiece.piece?.id}
            />
          </Box>
        ))}
      </div>
    </div>
  );
}

export default DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces;
